import CloudComputeScreenshot from '@/components/cloud/CloudComputeScreenshot';
import React from 'react';
import { Link } from '@/i18n/router';
import { useMessages } from '@/i18n/LocaleProvider';
import messages from '@/i18n/catalogs/corporate/CloudSection';
import { Button } from '@/components/ui/button';
import SectionHeading from '@/components/SectionHeading';
import {
  Cloud,
  Database,
  Building2,
  Table2,
  NotebookText,
  Plug,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';
import AnimatedSection from './AnimatedSection';

const CloudSection = () => {
  const m = useMessages(messages);
  const features: { icon: React.ElementType; label: string }[] = [
    { icon: Database, label: m.s1 },
    { icon: Building2, label: m.s2 },
    { icon: Table2, label: m.s3 },
    { icon: NotebookText, label: m.s4 },
    { icon: Plug, label: m.s5 },
    { icon: ShieldCheck, label: m.s6 },
  ];
  return (
    <section className="bg-background py-20 md:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fade-up">
          <div className="relative overflow-hidden rounded-3xl border border-gizmo-400/30 bg-gradient-to-br from-gizmo-500/10 via-background to-background p-8 md:p-12 shadow-sm">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gizmo-400/10 blur-3xl" />

            <div className="relative">
              <div className="flex flex-col items-center text-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-gizmo-400/40 bg-gizmo-500/10 px-4 py-1.5 text-sm font-semibold text-gizmo-700 dark:text-gizmo-300">
                  <Cloud className="h-4 w-4" />
                  GizmoData Cloud
                </span>
                <SectionHeading withId anchor="cloud" className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  {m.heading}
                </SectionHeading>
                <p className="mt-4 max-w-2xl text-xl text-muted-foreground">
                  {m.intro}
                </p>
              </div>

              <div className="mt-10 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
                <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-4 py-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                  <span className="ml-3 flex-1 truncate rounded-md bg-background/70 px-3 py-0.5 text-center text-xs text-muted-foreground">{m.shotUrl}</span>
                </div>
                <CloudComputeScreenshot
                  sizes="(min-width: 1024px) 60rem, calc(100vw - 4rem)"
                  alt={m.shotAlt}
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-full"
                />
              </div>
              <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3">
                {features.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3"
                  >
                    <Icon className="h-5 w-5 flex-shrink-0 text-gizmo-600 dark:text-gizmo-400" />
                    <span className="text-sm font-medium text-foreground">{label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col items-center gap-3">
                <Button asChild size="lg" variant="outline" className="h-auto max-w-full whitespace-normal py-3 text-center">
                  <Link to="/cloud" data-ga-cta="view_product" data-ga-location="home_cloud">
                    {m.seeAll}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <p className="text-sm text-muted-foreground">
                  {m.s14}
                  <Link to="/cloud/at-customer" className="underline underline-offset-4 hover:text-foreground" data-ga-cta="view_cloud_at_customer" data-ga-location="home_cloud">{m.s15}</Link>{m.s16}
                </p>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default CloudSection;
